import { Component, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Browser } from '@capacitor/browser';
import { Storage } from '@capacitor/storage';
import { App } from '@capacitor/app';

import {
  Platform,
  NavController,
  LoadingController,
  MenuController,
  ModalController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { PlayerService } from 'src/app/services/player.service';
import { TrainerService } from 'src/app/services/trainer.service';
import { NotificationType } from 'src/app/models/user';
import OneSignal from 'onesignal-cordova-plugin';
import { DeleteAccountComponent } from './components/delete-account/delete-account.component';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  loggedIn = false;
  language = 'de';
  readonly skillVideoReviewPage = 'tabs/profile/trainer/skill-video-review';
  readonly skillVideoRecordPage =
    'tabs/profile/player/skills/skill-video-record';

  constructor(
    private platform: Platform,
    public translate: TranslateService,
    private userService: UserService,
    public navCtrl: NavController,
    private playerService: PlayerService,
    private trainerService: TrainerService,
    private loadingCtrl: LoadingController,
    private router: Router,
    private zone: NgZone,
    private menuCtrl: MenuController,
    private modalCtrl: ModalController
  ) {
    this.initializeApp();
    this.userService.authState$.subscribe(
      (loggedIn) => (this.loggedIn = loggedIn)
    );
  }

  async openUrl(url: string) {
    const loading = await this.loadingCtrl.create();
    loading.present();
    await Browser.open({ url, toolbarColor: '#401769' });
    await loading.dismiss();
  }

  async changeLanguage() {
    this.translate.setDefaultLang(this.language);
    await Storage.set({ key: UserService.LANG_KEY, value: this.language });
  }

  async logout() {
    await this.userService.logout();
    this.navCtrl.navigateRoot('/account/login');
  }

  openSideMenu() {
    this.menuCtrl.toggle();
  }

  async initializeApp() {
    App.addListener('appUrlOpen', (data: { url: string }) => {
      this.zone.run(() => {
        let { url } = data;
        const navPath = new URL(url).pathname;
        const queryPath = new URL(url).search;
        url = navPath + queryPath;
        const urlQueryParams = this.router.parseUrl(url).queryParams;
        this.router.navigate([navPath], {
          queryParams: urlQueryParams,
          replaceUrl: true,
        });
      });
    });

    try {
      await this.platform.ready();
      document.body.classList.toggle('dark', true);
    } catch (err) {
      console.warn(err);
    }

    if (Capacitor.isNativePlatform()) {
      this.registerNotifications();
      StatusBar.setStyle({ style: Style.Dark });
      SplashScreen.hide();
    }

    const language =
      (await Storage.get({ key: UserService.LANG_KEY })).value || this.language;
    if (language) {
      this.language = language;
    }

    this.translate.addLangs(['en', 'de']);
    this.translate.setDefaultLang(language);
  }

  async handleNotificationOpened(data: any) {
    const {
      player_training_id,
      notification_type,
      custom_url,
    } = data.notification.payload.additionalData;
    switch (notification_type) {
      case NotificationType.VIDEO_RATED:
        this.router.navigate([this.skillVideoRecordPage, player_training_id]);
        break;
      case NotificationType.REVIEW_ASSIGNED:
        this.router.navigate([this.skillVideoReviewPage, player_training_id]);
        break;
      default:
        break;
    }
    if (custom_url) {
      await this.openUrl(custom_url);
    }
  }

  async registerNotifications() {
    if (!environment.production) {
      OneSignal.setLogLevel(6, 0);
    }

    OneSignal.setAppId(environment.oneSignalAppId);

    OneSignal.setNotificationOpenedHandler(function (data) {
      this.handleNotificationOpened(data);
    });

    // handle notifications opened on onse signal
    OneSignal.setNotificationOpenedHandler((data) => {
      this.handleNotificationOpened(data);
    });

    OneSignal.setNotificationWillShowInForegroundHandler((data) => {
      this.userService.onUpdate$.next();
      this.playerService.onUpdate$.next();
      this.trainerService.onUpdate$.next();
    });

    if (this.platform.is('ios')) {
      OneSignal.promptForPushNotificationsWithUserResponse(function (accepted) {
        console.log('User accepted notifications: ' + accepted);
      });
    }

    if (!environment.production) {
      OneSignal.addPermissionObserver((permissionChange) => {
        console.log('permissionChange: ', permissionChange);
      });
    }
  }

  async openDeleteAccountDialog() {
    const modal = await this.modalCtrl.create({
      component: DeleteAccountComponent,
      backdropDismiss: false,
    });

    modal.onDidDismiss().then((dataReturned) => {
      if (dataReturned.data.dismissed) {
        return;
      } else if (dataReturned.data?.status) {
        this.logout();
      }
    });
    return await modal.present();
  }
}
